import { useEffect } from 'react';

import { useRecoilState } from 'recoil';

import { simpleOpenAtomFamily } from '@shared/state/atom';

//* 모바일 환경에서 뒤로가기 누를 시 모달만 꺼지도록 — 모달 하나가 히스토리 항목 하나.
//*
//! 종전엔 popstate 한 번에 **마운트된 모든 모달**을 닫았다. 모달 위에 모달을 띄운 뒤 위 것만
//! 닫아도(X·뒤로가기 = history.back) 아래 것까지 같이 꺼졌고, 그때 headlessui Dialog의
//! 스크롤 잠금이 어긋난다: Dialog는 열릴 때 documentElement.style.overflow의 **이전 값**을
//! 저장했다가 닫힐 때 그대로 복원하는데, 둘이 같은 커밋에 닫히면 아래 모달(저장값 '')이 먼저
//! 복원하고 위 모달(저장값 'hidden')이 나중에 복원해 `overflow: hidden`이 남는다 → 페이지
//! 스크롤 불가. 그래서 (1) popstate는 **히스토리 스택 맨 위 모달만** 닫고, (2) 안전망으로
//! 열린 다이얼로그가 하나도 없는데 잠금이 남아 있으면 풀어준다.

type OpenSetter = (open: boolean) => void;

//* pushState 순서대로 쌓인 openId (맨 뒤가 최신). popstate 한 번 = 맨 위 하나 pop.
//* 프로그램으로 닫은 뒤 history.back()을 부르는 흐름도 같은 경로(pop → 이미 닫힌 모달에 no-op)라
//* 순서 문제가 없고, back() 없이 닫은 모달의 항목은 다음 뒤로가기가 조용히 소비한다.
const modalStack: string[] = [];
const openSetters = new Map<string, Set<OpenSetter>>();
const openIds = new Set<string>();
let popListenerAttached = false;

//* 스크롤 잠금 안전망 — 닫힘 전환(200ms)이 끝난 뒤 검사. headlessui Dialog(사이드바 등 훅 안 쓰는
//* 것 포함)가 DOM에 남아 있으면 정상 잠금이므로 손대지 않고 몇 번 더 기다린다.
const SCROLL_LOCK_REPAIR_DELAY = 400;
const SCROLL_LOCK_REPAIR_TRIES = 5;
let repairTimer: ReturnType<typeof setTimeout> | undefined;

const repairScrollLock = (triesLeft: number) => {
  repairTimer = undefined;
  if (openIds.size > 0) return;
  const root = document.documentElement;
  const locked = root.style.overflow === 'hidden' || root.style.position === 'fixed';
  if (!locked) return;
  if (document.querySelector('[role="dialog"]')) {
    if (triesLeft > 0) {
      repairTimer = setTimeout(
        () => repairScrollLock(triesLeft - 1),
        SCROLL_LOCK_REPAIR_DELAY,
      );
    }
    return;
  }
  //* iOS 분기(position:fixed + marginTop:-scrollY)까지 headlessui가 건드린 값 전부 원복
  const scrollY =
    root.style.position === 'fixed'
      ? Math.abs(parseInt(root.style.marginTop || '0', 10)) || 0
      : 0;
  ['overflow', 'padding-right', 'position', 'margin-top', 'width'].forEach((prop) =>
    root.style.removeProperty(prop),
  );
  if (scrollY) {
    try {
      window.scrollTo(0, scrollY);
    } catch {
      //* jsdom 등 미구현 환경 — 잠금 해제 자체는 이미 끝났다
    }
  }
};

const scheduleScrollLockRepair = () => {
  if (repairTimer) clearTimeout(repairTimer);
  repairTimer = setTimeout(
    () => repairScrollLock(SCROLL_LOCK_REPAIR_TRIES),
    SCROLL_LOCK_REPAIR_DELAY,
  );
};

const onPopState = () => {
  const openId = modalStack.pop();
  if (openId) {
    openSetters.get(openId)?.forEach((setOpen) => setOpen(false));
  }
  scheduleScrollLockRepair();
};

export const useModalHook = (openId: string) => {
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));

  //* 세터 등록 — popstate가 맨 위 모달 하나만 찾아 닫을 수 있게 (같은 openId 중복 마운트 허용)
  useEffect(() => {
    let setters = openSetters.get(openId);
    if (!setters) {
      setters = new Set();
      openSetters.set(openId, setters);
    }
    setters.add(setOpen);
    if (!popListenerAttached) {
      window.addEventListener('popstate', onPopState);
      popListenerAttached = true;
    }
    return () => {
      setters?.delete(setOpen);
      if (setters && setters.size === 0) openSetters.delete(openId);
    };
  }, [openId, setOpen]);

  useEffect(() => {
    if (open) {
      history.pushState({ page: 'modal', openId }, openId);
      modalStack.push(openId);
      openIds.add(openId);
      return () => {
        //* 닫힘(또는 언마운트) — 스택 항목은 popstate가 소비하므로 여기선 열림 집합만 갱신
        openIds.delete(openId);
        scheduleScrollLockRepair();
      };
    }
    return undefined;
  }, [open, openId]);
};
